/**
 * DesktopLayout
 *
 * Wraps the entire app in a flex-row that shows the DesktopSidebar on the
 * left when the window is wide enough. On narrow windows (< 1024px) it
 * renders children full-screen (identical to the native mobile app).
 *
 * Also:
 *  - Hides the loading splash on first render (web only)
 *  - Listens for Electron notification and native-menu events
 *
 * Placement: wraps <NavigationContainer> + <RootNavigator> inside App.tsx.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, useWindowDimensions, StyleSheet, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { getElectronBridge, DESKTOP_BREAKPOINT, WIDGET_GUTTER, isWeb, isElectron } from '../utilities/platform';
import { navigationRef } from '../navigation/navigationRef';
import { trackWebNotificationOpen } from '../navigation/notificationTelemetry';
import { reconcileWebPush } from '../services/webPush';
import { useTheme } from '../hooks/useTheme';
import DesktopSidebar from './DesktopSidebar';
import DesktopLockScreen from './DesktopLockScreen';
import DesktopUpdateBanner from './DesktopUpdateBanner';
import WebUpdateBanner from './WebUpdateBanner';
import { SET_LOCKED } from '../store/constants';
import { hexToRgba } from '../utilities/color';
import { TourProvider } from '../tour/TourContext';
import TourOverlay from '../tour/TourOverlay';
import type { RootState } from '../store';

// Widget frame for desktop layouts. The window itself is the backdrop (a
// soft brand-tinted gradient); the widget is a centered, max-width rounded
// card that holds the entire app (icon-rail sidebar + content). Below the
// desktop breakpoint we render full-bleed and skip the widget treatment so
// touch/handheld layouts look identical to the native mobile app.
// The gutter lives in utilities/platform (single source of truth) — the
// Notification toast and both update banners anchor inside the frame with it.
const WIDGET_MAX_WIDTH = 1400;

interface Props {
  children: React.ReactNode;
}

export default function DesktopLayout({ children }: Props) {
  const { width } = useWindowDimensions();
  const theme = useTheme();
  const showSidebar = width >= DESKTOP_BREAKPOINT;
  const showWidgetFrame = showSidebar; // Same threshold — they travel together.

  // Two independent lock signals:
  //   1. `electronLocked` — Electron-driven (Cmd+L, OS sleep, idle timer fired
  //      in main.ts). Drives the dedicated DesktopLockScreen overlay below.
  //   2. `inAppLocked` — the cross-platform in-app PIN/biometric LockScreen
  //      (mobile/src/components/LockScreen.tsx) which lives INSIDE the
  //      navigator tree. It overlays the content area only, so without this
  //      flag the desktop sidebar would stay clickable behind it.
  // Either one collapses the sidebar so no nav is reachable while locked.
  const [electronLocked, setElectronLocked] = useState(false);
  const inAppLocked = useSelector((s: RootState) => !!s.session?.locked?.show);
  const locked = electronLocked || inAppLocked;
  const dispatch = useAppDispatch();

  const handleUnlock = useCallback(() => {
    const bridge = getElectronBridge();
    bridge?.unlockScreen();
    setElectronLocked(false);
    // The two lock layers can both be active at once (e.g. main fires the
    // idle/system lock while the renderer's AppState bg→active grace
    // expired in the same window). Clearing only `electronLocked` would
    // leave the user staring at the in-app LockScreen right after they
    // already authenticated through DesktopLockScreen — one credential
    // entry should drop both layers.
    dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } });
  }, [dispatch]);

  // Refs for imperative -webkit-app-region assignment. React's inline style
  // system does not recognise WebkitAppRegion and silently drops it, so we
  // set the CSS property directly on the DOM nodes after each render.
  const rootRef    = useRef<any>(null);
  const widgetRef  = useRef<any>(null);
  const dragStripRef = useRef<any>(null);

  useEffect(() => {
    if (!isElectron) return;
    const root = rootRef.current;
    if (root?.style) {
      // Wide layout: the WIDGET_GUTTER padding outside the widget is the
      // drag zone. Narrow layout: drag comes from the drag strip below.
      (root.style as any).webkitAppRegion = showWidgetFrame ? 'drag' : '';
    }
    const widget = widgetRef.current;
    if (widget?.style) {
      // The interactive content area must never be draggable so mouse events
      // (clicks, text selection, scrolling) are unaffected.
      (widget.style as any).webkitAppRegion = 'no-drag';
    }
    const strip = dragStripRef.current;
    if (strip?.style) {
      // Narrow-window fallback: 22 px transparent strip at the very top.
      (strip.style as any).webkitAppRegion = showWidgetFrame ? '' : 'drag';
    }
  }, [showWidgetFrame]);

  // ── Electron: listen for lock events from main process ────────────────────
  useEffect(() => {
    if (!isElectron) return;
    const bridge = getElectronBridge();
    if (!bridge?.onLockScreen) return;
    const unsub = bridge.onLockScreen(() => setElectronLocked(true));
    return unsub;
  }, []);

  // ── Hide splash screen on first web render ────────────────────────────────
  useEffect(() => {
    if (isWeb && typeof window !== 'undefined' && typeof window.__hideSplash === 'function') {
      // Small delay lets React finish painting before removing splash
      const t = setTimeout(window.__hideSplash, 150);
      return () => clearTimeout(t);
    }
  }, []);

  // ── Electron: handle native menu navigation (e.g. Preferences ⌘,) ────────
  useEffect(() => {
    const bridge = getElectronBridge();
    if (!bridge?.onNavigateTo) return;

    const unsub = bridge.onNavigateTo(({ screen }: { screen: string }) => {
      if (!navigationRef.isReady()) return;
      try {
        const rootScreens = new Set(['Login', 'Register', 'MFA', 'ForcePassword', 'Accounts', 'Main']);
        if (rootScreens.has(screen)) {
          (navigationRef as any).navigate(screen);
        } else {
          (navigationRef as any).navigate('Main', { screen });
        }
      } catch {
        // Navigation best-effort; ignore if stack not ready
      }
    });
    return unsub;
  }, []);

  // ── Web push: handle service-worker notification clicks ───────────────────
  useEffect(() => {
    if (!isWeb || typeof navigator === 'undefined' || !navigator.serviceWorker) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
        // The service worker deliberately cannot recreate/register endpoints:
        // it has neither authenticated identity nor the user's durable opt-in.
        // Reconcile here, where both are available. The helper never prompts
        // and will not recreate for an absent/disabled preference.
        void reconcileWebPush();
        return;
      }
      if (event.data?.type !== 'NOTIFICATION_CLICKED') return;

      const { projectId, category, smartKey, pageId } = event.data ?? {};

      // Count the open before the readiness gate. A click that lands while the
      // navigator is still mounting is still an open — bailing out first would
      // silently under-report exactly the cold-ish opens we most want to see.
      trackWebNotificationOpen(category);

      if (!navigationRef.isReady()) return;

      try {
        if (category === 'reminder' && projectId && smartKey) {
          (navigationRef as any).navigate('Main', {
            screen: 'Plans',
            params: {
              screen: 'ProjectSmartReminders',
              params: { projectId, detailSmartKey: smartKey, detailPageId: pageId },
            },
          });
        } else if (category === 'health_records_reconnect' && projectId) {
          (navigationRef as any).navigate('Main', {
            screen: 'Plans',
            params: { screen: 'ProjectFasten', params: { projectId } },
          });
        } else if (projectId) {
          (navigationRef as any).navigate('Main', {
            screen: 'Plans',
            params: { screen: 'ProjectHome', params: { projectId } },
          });
        }
      } catch {
        // Navigation best-effort; ignore if not ready
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  // Brand-tinted backdrop — only painted when the widget frame is active.
  // Gradient mixes a low-opacity electricBlue wash into the theme background
  // so the widget visually pops via the contrast gradient + box-shadow.
  // Hex strings are converted to rgba inline so we don't carry a one-off
  // color helper alongside OrbAvatar's identical conversion.
  const electricBlue = (theme.colors as any).electricBlue || '#06B6D4';
  const bg = theme.colors.background;
  const backdropStyle = showWidgetFrame ? {
    ...Platform.select({
      web: {
        backgroundImage: `linear-gradient(135deg, ${hexToRgba(electricBlue, 0.10)} 0%, ${bg} 55%, ${bg} 100%)`,
      } as any,
      default: { backgroundColor: bg },
    }),
  } : { backgroundColor: bg };

  // Widget shell: rounded card, hairline border, soft drop shadow on web.
  // We don't try to mirror the shadow on native — DesktopLayout is web/electron
  // only in practice (handheld widths skip the widget treatment entirely).
  const widgetStyle = showWidgetFrame ? [
    styles.widget,
    {
      backgroundColor: bg,
      borderColor: theme.colors.border,
    },
    Platform.OS === 'web' ? ({
      boxShadow: '0 24px 60px rgba(0, 0, 0, 0.18), 0 6px 18px rgba(0, 0, 0, 0.10)',
    } as any) : null,
  ] : [
    styles.widgetFlush,
  ];

  return (
    // The tour provider wraps the sidebar (which hosts its launcher) and the
    // whole content area, and the overlay renders last so its spotlight paints
    // above every screen. Native never mounts DesktopLayout, so the tour is
    // desktop/web-only by construction rather than by a platform check.
    <TourProvider>
    <View ref={rootRef} style={[styles.root, showWidgetFrame ? styles.rootWithFrame : styles.rootFlush, backdropStyle]}>
      <View ref={widgetRef} style={widgetStyle}>
        {/* Hide sidebar whenever ANY lock is active so plan names / nav
            structure aren't leaked behind the lock UI. The in-app LockScreen
            renders inside the navigator (i.e. inside `content` below) and
            otherwise wouldn't extend over the sidebar's column. */}
        {showSidebar && !locked && <DesktopSidebar />}
        <View style={[styles.content, showSidebar && !locked && styles.contentWithSidebar]}>
          {children}
        </View>
      </View>
      {/* Narrow Electron windows have no gutter to grab — add a thin
          drag strip pinned to the very top of the window. 22px keeps it
          out of the way of typical header content (back buttons usually
          sit ≥ 28px from the top once safe-area + header padding is
          applied) while still being easy to grab. The strip itself is
          transparent and doesn't paint anything.
          -webkit-app-region is applied imperatively via dragStripRef. */}
      {isElectron && !showWidgetFrame && (
        <View ref={dragStripRef} style={styles.electronDragStrip} />
      )}
      {/* Single desktop unlock UI. Mobile's LockScreen is gated off in
          Electron (see RootNavigator), so DesktopLockScreen owns BOTH lock
          signals here:
            - `electronLocked` — main process: Cmd+L, OS sleep, idle timer
            - `inAppLocked`    — JS: bg→active inactivity, manual Lock Now,
                                 auth bootstrap forcing a re-lock
          handleUnlock above clears both, so a single PIN/biometric entry
          drops whichever combination of layers happened to be active. */}
      {isElectron && (electronLocked || inAppLocked) && (
        <DesktopLockScreen onUnlock={handleUnlock} />
      )}
      {/* electron-updater downloads new versions silently in the background,
          then fires `update-available`. The banner owns its own subscription
          so this layer doesn't have to thread visibility state around. It
          renders nothing outside Electron, so it's a no-op in the browser. */}
      <DesktopUpdateBanner />
      {/* Browser/web equivalent: the service worker registered in
          mobile/web/index.html (`updateViaCache: 'none'`) fires
          `updatefound` when a new deploy lands on S3. useWebUpdateMonitor
          (inside WebUpdateBanner) listens for the new worker reaching
          'installed' state with a previous controller already in place
          (i.e. it's an update, not a first install) and surfaces this
          toast. Reload swaps the document for the fresh index.html with
          the new bundle manifest. Renders nothing in Electron or on
          native — DesktopUpdateBanner covers the Electron surface, and
          neither surface exists on iOS/Android. */}
      <WebUpdateBanner />
      {/* Last child of the root: the spotlight has to paint over the sidebar,
          the widget frame, and whatever screen is mounted. */}
      <TourOverlay />
    </View>
    </TourProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    // On web, the app fills the viewport.
    ...Platform.select({
      web: { height: '100vh' as any, overflow: 'hidden' } as any,
      default: {},
    }),
  },
  // Active when ≥ DESKTOP_BREAKPOINT — adds the centering + gutter that lets
  // the widget float in the middle of the backdrop.
  rootWithFrame: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: WIDGET_GUTTER,
  },
  // Below the breakpoint: full-bleed flex row so the layout matches native.
  rootFlush: {
    flexDirection: 'row',
  },
  // Desktop widget shell.
  widget: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    maxWidth: WIDGET_MAX_WIDTH,
    borderRadius: 18,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  widgetFlush: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  contentWithSidebar: {
    // Content takes whatever space remains after the sidebar; flex:1 handles it.
  },
  electronDragStrip: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 22,
    // No background — drag region is invisible. zIndex must beat any
    // header content that paints in the same area so the OS picks up
    // the drag intent before children see the mousedown.
    zIndex: 1000,
  },
});
